import React, { createRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import Table from 'react-virtualized/dist/commonjs/Table/Table';
import Column from 'react-virtualized/dist/commonjs/Table/Column';
import Draggable from 'react-draggable';
// import Loader from './Loader';
import 'react-virtualized/styles.css';

function DataTableVirtualized({
  columns,
  data,
  minusHeight,
  striped,
  hover,
  customHeight,
  hasFooter,
  addStyle,
  ...rest
}) {
  const tableRef = createRef();

  const [widths, setWidths] = useState({});
  const [totalWidth, setTotalWidth] = useState(0);

  useEffect(() => {
    const defaultWidths = columns.reduce((currrent, accumulator) => {
      return {
        ...currrent,
        [accumulator.dataKey]: accumulator.width,
      };
    }, {});

    setWidths((prevState) => ({
      ...prevState,
      ...defaultWidths,
    }));
  }, [columns]);

  const pcustomHeight = (autoHeight) => {
    return customHeight(autoHeight);
  };

  const resizeRow = ({ dataKey, deltaX }) =>
    setWidths((prevWidths) => {
      const percentDelta = deltaX / totalWidth;
      const dataKeyIndex = columns.findIndex(
        (column) => column.dataKey === dataKey
      );
      const nextData = columns[dataKeyIndex + 1];

      if (nextData && typeof nextData.resizable === 'undefined') {
        const nextDataKey = nextData.dataKey;
        return {
          ...prevWidths,
          [dataKey]: prevWidths[dataKey] + percentDelta,
          [nextDataKey]: prevWidths[nextDataKey] - percentDelta,
        };
      }

      return {
        ...prevWidths,
        [dataKey]: prevWidths[dataKey] + percentDelta,
      };
    });

  const headerRenderer = (options) => {
    return (
      <React.Fragment key={options.dataKey}>
        <div className="ReactVirtualized__Table__headerTruncatedText h-full">
          {options.label}
        </div>
        <Draggable
          axis="x"
          defaultClassName="DragHandle"
          defaultClassNameDragging="DragHandleActive"
          onDrag={(event, { deltaX }) =>
            resizeRow({
              dataKey: options.dataKey,
              deltaX,
            })
          }
          position={{ x: 0 }}
          zIndex={999}
        >
          <span className="DragHandleIcon">⋮</span>
        </Draggable>
      </React.Fragment>
    );
  };

  const onResize = ({ width }) => {
    setTotalWidth(width);
  };

  return (
    <AutoSizer onResize={onResize}>
      {({ width, height }) => {
        if (totalWidth === 0) {
          return <span>Loading...</span>;
        }

        let finalHeight = height;
        if (hasFooter) {
          finalHeight -= 40;
        }

        return (
          <>
            <Table
              ref={tableRef}
              rowClassName={`border-b text-sm ${
                hover ? 'hover:bg-gray-200' : ''
              }`}
              headerHeight={50}
              rowHeight={50}
              height={customHeight ? pcustomHeight(finalHeight) : finalHeight}
              rowGetter={({ index }) => {
                return data[index];
              }}
              rowCount={data.length}
              width={width - 3}
              style={addStyle}
              rowStyle={({ index }) => {
                if (index % 2 === 0 && striped) {
                  return {
                    backgroundColor: 'var(--gray-100)',
                  };
                }
                return {};
              }}
              {...rest}
            >
              {columns.map((item) => {
                if (item.resizable === false) {
                  return (
                    <Column
                      key={`dataColums${item.dataKey}`}
                      {...item}
                      width={widths[item.dataKey] * totalWidth}
                    />
                  );
                }
                return (
                  <Column
                    key={`dataColums${item.dataKey}`}
                    headerRenderer={headerRenderer}
                    {...item}
                    width={widths[item.dataKey] * totalWidth}
                  />
                );
              })}
            </Table>
            {hasFooter && (
              <div
                className={`flex ${rest.footerClassName}`}
                style={{ width: `${width}px`, marginTop: -5, height: 40 }}
              >
                {columns.map((item) => {
                  return (
                    <div
                      className={`flex items-center ${item.footerClassName}`}
                      style={{
                        width: `${widths[item.dataKey] * totalWidth}px`,
                      }}
                    >
                      {item.footerRenderer && item.footerRenderer()}
                      {item.footerValue && item.footerValue}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        );
      }}
    </AutoSizer>
  );
}

DataTableVirtualized.defaultProps = {
  columns: [],
  data: [],
  minusHeight: 0,
  striped: true,
  hover: true,
  customHeight: null,
  hasFooter: false,
  addStyle: {},
};

DataTableVirtualized.propTypes = {
  data: PropTypes.instanceOf(Array),
  minusHeight: PropTypes.number,
  striped: PropTypes.bool,
  hover: PropTypes.bool,
  customHeight: PropTypes.instanceOf(Function),
  columns: PropTypes.instanceOf(Array),
  hasFooter: PropTypes.bool,
  addStyle: PropTypes.instanceOf({}),
};

export default DataTableVirtualized;
